import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Users, MousePointer, DollarSign, Calendar, Filter, Download, RefreshCw, AlertCircle, CheckCircle, Target, Globe } from 'lucide-react';

const FunnelAnalytics = ({ funnelData }) => {
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('conversions');
  const [isLoading, setIsLoading] = useState(false);

  // Mock analytics data - in real implementation, this would come from your analytics service
  const analyticsData = {
    overview: {
      totalVisitors: 12547,
      totalConversions: 1876,
      conversionRate: 14.95,
      revenue: 47820,
      trends: {
        visitors: 12.5,
        conversions: 8.3,
        conversionRate: -2.1,
        revenue: 15.7
      }
    },
    stageMetrics: [
      {
        stage: 'Landing Page',
        visitors: 12547,
        conversions: 6274,
        conversionRate: 50.0,
        dropOff: 6273
      },
      {
        stage: 'Lead Magnet',
        visitors: 6274,
        conversions: 3137,
        conversionRate: 50.0,
        dropOff: 3137
      },
      {
        stage: 'Email Sequence',
        visitors: 3137,
        conversions: 2196,
        conversionRate: 70.0,
        dropOff: 941
      },
      {
        stage: 'Sales Page',
        visitors: 2196,
        conversions: 1876,
        conversionRate: 85.4,
        dropOff: 320
      }
    ],
    channelPerformance: [
      { channel: 'Organic Search', visitors: 4518, conversions: 678, revenue: 16940 },
      { channel: 'Social Media', visitors: 3274, conversions: 524, revenue: 13100 },
      { channel: 'Email Marketing', visitors: 2891, conversions: 463, revenue: 11575 },
      { channel: 'Paid Ads', visitors: 1864, conversions: 211, revenue: 6205 }
    ],
    timeSeriesData: [
      { date: '2025-01-01', visitors: 450, conversions: 68 },
      { date: '2025-01-02', visitors: 520, conversions: 78 },
      { date: '2025-01-03', visitors: 480, conversions: 72 },
      { date: '2025-01-04', visitors: 390, conversions: 59 },
      { date: '2025-01-05', visitors: 610, conversions: 92 },
      { date: '2025-01-06', visitors: 580, conversions: 87 },
      { date: '2025-01-07', visitors: 540, conversions: 81 }
    ]
  };

  const refreshData = () => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1000);
  };

  const formatNumber = (num) => {
    return num?.toLocaleString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    })?.format(amount);
  };

  const getTrendIcon = (trend) => {
    return trend > 0 ? (
      <TrendingUp className="w-4 h-4 text-green-500" />
    ) : (
      <TrendingUp className="w-4 h-4 text-red-500 transform rotate-180" />
    );
  };

  const getTrendColor = (trend) => {
    return trend > 0 ? 'text-green-600' : 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Funnel Analytics</h2>
          <p className="text-gray-600">Track performance and optimize your marketing funnel</p>
        </div>
        
        <div className="flex items-center space-x-4 mt-4 lg:mt-0">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e?.target?.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          
          <button
            onClick={refreshData}
            disabled={isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>
      {/* Key Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          {
            label: 'Total Visitors',
            value: formatNumber(analyticsData?.overview?.totalVisitors),
            trend: analyticsData?.overview?.trends?.visitors,
            icon: Users,
            color: 'bg-blue-500'
          },
          {
            label: 'Conversions',
            value: formatNumber(analyticsData?.overview?.totalConversions),
            trend: analyticsData?.overview?.trends?.conversions,
            icon: Target,
            color: 'bg-green-500'
          },
          {
            label: 'Conversion Rate',
            value: `${analyticsData?.overview?.conversionRate}%`,
            trend: analyticsData?.overview?.trends?.conversionRate,
            icon: MousePointer,
            color: 'bg-purple-500'
          },
          {
            label: 'Revenue',
            value: formatCurrency(analyticsData?.overview?.revenue),
            trend: analyticsData?.overview?.trends?.revenue,
            icon: DollarSign,
            color: 'bg-orange-500'
          }
        ]?.map((metric, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg p-6 shadow-sm"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg ${metric?.color}`}>
                <metric.icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex items-center space-x-1">
                {getTrendIcon(metric?.trend)}
                <span className={`text-sm font-medium ${getTrendColor(metric?.trend)}`}>
                  {Math.abs(metric?.trend)}%
                </span>
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">{metric?.value}</div>
            <div className="text-sm text-gray-600">{metric?.label}</div>
          </motion.div>
        ))}
      </div>
      {/* Funnel Visualization */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Funnel Performance</h3>
        
        <div className="space-y-4">
          {analyticsData?.stageMetrics?.map((stage, index) => {
            const widthPercentage = (stage?.visitors / analyticsData?.stageMetrics?.[0]?.visitors) * 100;
            
            return (
              <div key={index} className="relative">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{stage?.stage}</h4>
                  <div className="text-sm text-gray-600">
                    {formatNumber(stage?.conversions)} / {formatNumber(stage?.visitors)} 
                    ({stage?.conversionRate}%)
                  </div>
                </div>
                <div className="relative">
                  <div className="w-full bg-gray-200 rounded-full h-8">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${widthPercentage}%` }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                      className="bg-gradient-to-r from-purple-500 to-blue-500 h-8 rounded-full flex items-center justify-center"
                    >
                      <span className="text-white text-sm font-medium">
                        {formatNumber(stage?.visitors)}
                      </span>
                    </motion.div>
                  </div>
                  
                  {index < analyticsData?.stageMetrics?.length - 1 && (
                    <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                      -{formatNumber(stage?.dropOff)} drop-off
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Channel Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Channel Performance</h3>
          
          <div className="space-y-4">
            {analyticsData?.channelPerformance?.map((channel, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    index === 0 ? 'bg-blue-500' :
                    index === 1 ? 'bg-green-500' :
                    index === 2 ? 'bg-purple-500' : 'bg-orange-500'
                  }`}></div>
                  <span className="font-medium text-gray-900">{channel?.channel}</span>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-gray-900">{formatCurrency(channel?.revenue)}</div>
                  <div className="text-sm text-gray-600">
                    {formatNumber(channel?.conversions)} conversions
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Insights */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Performance Insights</h3>
          
          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-green-900">High Conversion Rate</h4>
                <p className="text-sm text-green-700">
                  Your email sequence is performing excellently with a 70% conversion rate.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 p-4 bg-orange-50 rounded-lg">
              <AlertCircle className="w-5 h-5 text-orange-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-900">Optimization Opportunity</h4>
                <p className="text-sm text-orange-700">
                  Consider A/B testing your landing page to improve the initial conversion rate.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
              <Globe className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Traffic Growth</h4>
                <p className="text-sm text-blue-700">
                  Organic search traffic has increased by 12.5% this month.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Time Series Chart Placeholder */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Conversion Trends</h3>
        
        <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
          <div className="text-center text-gray-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-2" />
            <p>Interactive chart would be displayed here</p>
            <p className="text-sm">Showing conversion trends over time</p>
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="flex items-center justify-center space-x-2 p-4 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors">
            <Target className="w-5 h-5" />
            <span>Set Conversion Goal</span>
          </button>
          
          <button className="flex items-center justify-center space-x-2 p-4 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors">
            <Filter className="w-5 h-5" />
            <span>Create Segment</span>
          </button>
          
          <button className="flex items-center justify-center space-x-2 p-4 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 transition-colors">
            <Calendar className="w-5 h-5" />
            <span>Schedule Report</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default FunnelAnalytics;